import { useState, useEffect, useCallback, useRef } from "react";
import {
  Avatar,
  Dialog,
  Box,
  CircularProgress,
  Fade,
  IconButton,
  Typography,
  Button,
  SwipeableDrawer,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import type { Story, StoryGroup } from "Components/Stories/StoriesSection";
import { getStoryAvatarSrc } from "Components/Stories/StoryAvatar";
import { StoryProgressBars } from "Components/Stories/StoryProgressBars";
import { useIsMobile } from "Hooks/useIsMobile";
import { Resources, useResource } from "Translations/Resources";
import {
  getEntryStoryIndex,
  prefetchStoryMedia,
} from "Utils/Stories/storyMediaPrefetch";

type Props = {
  storyGroup: StoryGroup;
  isOpen: boolean;
  /** Fade-out in progress — the parent unmounts only after onExited. */
  isClosing: boolean;
  /** User closed the viewer (X / backdrop) — the parent starts the fade-out (isClosing). */
  onClose: () => void;
  /** The group's last story finished — the parent decides whether to continue with another group. */
  onComplete: () => void;
  /** Tapped back on the group's FIRST story — the parent may switch to the previous group. */
  onPreviousSource: () => void;
  /** Reports every story actually displayed — the parent marks exactly these read on Stream. */
  onStoryViewed: (story: Story) => void;
  /** The shrink-back animation finished — the parent may unmount and finish the session. */
  onExited: () => void;
  /** Entered by navigating backwards — start at the last story instead of the first unread one. */
  shouldStartAtEnd?: boolean;
};

/** Press shorter than this navigates; holding longer pauses until release (Instagram gesture). */
const HOLD_THRESHOLD_MS = 250;

/**
 * An image that hasn't loaded by then is skipped (goNext) — same spirit as onError: a slow or
 * dead CDN must stall one story for a bounded time, never freeze the whole session.
 */
const IMAGE_LOAD_TIMEOUT_MS = 10_000;

export const StoryViewer = ({
  storyGroup,
  isOpen,
  isClosing,
  onClose,
  onComplete,
  onPreviousSource,
  onStoryViewed,
  onExited,
  shouldStartAtEnd = false,
}: Props) => {
  // Entry point: last story when arriving backwards from the next source; otherwise the first
  // unseen story (stories are ordered oldest → newest; an all-seen source replays from the start).
  // The component stays mounted across source switches (the Dialog must not close between
  // sources), so the same computation re-runs in the source-change reset effect below.
  const getEntryIndex = () =>
    shouldStartAtEnd
      ? storyGroup.stories.length - 1
      : getEntryStoryIndex(storyGroup);
  const [currentIndex, setCurrentIndex] = useState(getEntryIndex);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // isPaused: explicit toggle via the desktop pause/play button; isHolding: transient
  // press-and-hold gesture (any pointer type). Either one freezes the autoplay timer.
  const [isPaused, setIsPaused] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  // Session-scoped: survives story/source switches, resets on the next viewer open. Also set by
  // the muted-autoplay fallback below, so the toggle always reflects the element's real state.
  const [isMuted, setIsMuted] = useState(false);
  const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The video element as STATE (callback ref), not a ref object: MUI's Portal renders null on its
  // first render and commits the Dialog's children one render later, so a mount-time effect
  // reading a ref would see null and never re-run — the entry-on-video story would never play.
  // With the element in state, its attachment re-triggers the play effect.
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null,
  );
  // Keyed by story id and compared against the CURRENT story, so a story change "resets" it by
  // derivation — no reset effect, no frame pairing story A with story B's state.
  // readyImageStoryId: the image story whose <img> has actually painted (timer + viewed gate).
  const [readyImageStoryId, setReadyImageStoryId] = useState<string | null>(
    null,
  );
  const isMobile = useIsMobile();
  const { t } = useResource();

  // Source switch (same mounted Dialog — no close/reopen flash): re-run the entry-point logic
  // and reset per-source playback state. Done DURING render (React's props-derived state reset
  // pattern) so no intermediate render pairs the new source with the old index — the viewed
  // reporting effect below must never see that combination. Keyed on actorId so identity-only
  // refreshes of the group object (e.g. actor enrichment resolving mid-session) don't yank the
  // position; isPaused deliberately survives — a paused session stays paused across sources.
  const [renderedActorId, setRenderedActorId] = useState(storyGroup.actorId);
  const isSourceSwitch = renderedActorId !== storyGroup.actorId;
  if (isSourceSwitch) {
    setRenderedActorId(storyGroup.actorId);
    setIsHolding(false);
    setIsSheetOpen(false);
  }

  // ONE source of truth for the index adjustment: the entry point on a source switch, otherwise
  // the live-feed clamp (the SDK state can drop stories mid-view on 24 h TTL expiry — render the
  // last remaining story instead of nothing). These must never queue separately: during a switch
  // render, currentIndex still holds the PREVIOUS actor's position, and clamping it against the
  // new (smaller) group would enqueue a second update that overwrites the entry index — entering
  // e.g. a 2-story source from the 5th story of the previous one used to skip to its 2nd story.
  const totalStories = storyGroup.stories.length;
  const targetIndex = isSourceSwitch ? getEntryIndex() : currentIndex;
  const safeIndex = Math.max(0, Math.min(targetIndex, totalStories - 1));
  if (currentIndex !== safeIndex) {
    setCurrentIndex(safeIndex);
  }

  const story = storyGroup.stories[safeIndex];

  useEffect(
    () => () => {
      if (holdTimeoutRef.current !== null) {
        clearTimeout(holdTimeoutRef.current);
      }
    },
    [],
  );

  const goNext = useCallback(() => {
    if (safeIndex < totalStories - 1) {
      setCurrentIndex(safeIndex + 1);
    } else {
      onComplete();
    }
  }, [safeIndex, totalStories, onComplete]);

  const goPrev = useCallback(() => {
    if (safeIndex > 0) {
      setCurrentIndex(safeIndex - 1);
    } else {
      onPreviousSource();
    }
  }, [safeIndex, onPreviousSource]);

  const isPlaybackSuspended =
    !isOpen || isClosing || isSheetOpen || isPaused || isHolding;
  const isVideoStory = story?.videoUrl != null;
  const isImageStory = !isVideoStory && story?.imageUrl != null;
  const isImageReady = isImageStory && readyImageStoryId === story?.id;
  const isAwaitingImage = isImageStory && !isImageReady;

  // A story counts as VIEWED only once its media actually displayed — images on load, videos on
  // first playback (effect below). A story skipped while still black (stall timeout, dead URL)
  // stays unread, consistent with "stories never reached stay unread".
  useEffect(() => {
    if (story && isImageReady) {
      onStoryViewed(story);
    }
  }, [story, isImageReady, onStoryViewed]);

  useEffect(() => {
    if (!story || !isVideoStory || !videoElement) {
      return;
    }
    const handlePlaying = () => onStoryViewed(story);
    videoElement.addEventListener("playing", handlePlaying, { once: true });
    return () => videoElement.removeEventListener("playing", handlePlaying);
  }, [story, isVideoStory, videoElement, onStoryViewed]);

  // Progress state, its ticking and the image-story auto-advance live in StoryProgressBars —
  // isolating the 10 Hz updates there keeps this fullscreen tree from re-rendering every tick.

  // Video stories: every pause source (desktop button, hold gesture, product sheet, closing
  // animation) also pauses the element; resuming plays with sound — the viewer always opens
  // from a tap, so audible autoplay is allowed — with a muted retry if the browser refuses.
  // Keyed on the ELEMENT: it remounts per story (key={story.id}), so a video→video advance
  // re-plays the fresh element, and the element attaching at all (one commit after mount,
  // thanks to the Dialog's Portal) triggers the first play.
  useEffect(() => {
    if (!videoElement) {
      return;
    }
    if (isPlaybackSuspended) {
      videoElement.pause();
      return;
    }
    videoElement.play().catch(() => {
      videoElement.muted = true;
      setIsMuted(true);
      videoElement.play().catch(() => {
        // Even muted playback refused — the story stays paused; the user can tap next.
      });
    });
  }, [isPlaybackSuspended, videoElement]);

  // Bounded wait for a slow image: the progress timer is held while the image loads (see
  // StoryProgressBars), so without this a hanging request would freeze the story forever.
  // goNext changes with safeIndex, so the countdown restarts per story.
  useEffect(() => {
    if (!isAwaitingImage || isPlaybackSuspended) {
      return;
    }
    const timeoutId = setTimeout(goNext, IMAGE_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeoutId);
  }, [isAwaitingImage, isPlaybackSuspended, goNext]);

  // One-ahead warmup while the current story plays (CDN-only, deduped, Save-Data aware);
  // the NEXT SOURCE's entry is warmed by StoriesSection when this group nears its end.
  const nextStory = storyGroup.stories[safeIndex + 1];
  useEffect(() => {
    prefetchStoryMedia(nextStory);
  }, [nextStory]);

  // Press-and-hold pauses (any pointer type — touch on mobile, mouse-hold on desktop, like
  // Instagram); a release before the hold threshold counts as a tap and navigates by x-position.
  const handlePointerDown = () => {
    holdTimeoutRef.current = setTimeout(() => {
      holdTimeoutRef.current = null;
      setIsHolding(true);
    }, HOLD_THRESHOLD_MS);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (holdTimeoutRef.current !== null) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      if (x < rect.width / 2) {
        goPrev();
      } else {
        goNext();
      }
      return;
    }
    setIsHolding(false);
  };

  const handlePointerCancel = () => {
    if (holdTimeoutRef.current !== null) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    setIsHolding(false);
  };

  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSheetOpen(true);
  };

  // A dead URL would otherwise show 5 s of black frame (or stall forever now that the timer
  // waits for the load) — skip to the next story instead.
  const handleImageError = () => {
    goNext();
  };

  const handleMuteToggle = () => {
    const willBeMuted = !isMuted;
    setIsMuted(willBeMuted);
    // Unmuting is a user gesture — also revive a video that a fully-refused audible autoplay
    // left paused (the click satisfies the browser's activation requirement).
    if (!willBeMuted && videoElement?.paused && !isPlaybackSuspended) {
      videoElement.play().catch(() => undefined);
    }
  };

  const stopPointerPropagation = (e: React.PointerEvent) => {
    e.stopPropagation();
  };

  if (!story) {
    return null;
  }

  return (
    <>
      <Dialog
        // MUI's default Fade handles both directions; closing flips `open` while the parent
        // keeps the viewer mounted and finishes the session on the transition's onExited.
        open={isOpen && !isClosing}
        onClose={onClose}
        fullScreen
        slotProps={{
          transition: {
            onExited: () => onExited(),
          },
          paper: {
            sx: {
              bgcolor: "black",
            },
          },
        }}
      >
        <StoryProgressBars
          totalStories={totalStories}
          currentIndex={safeIndex}
          actorId={storyGroup.actorId}
          storyId={story.id}
          isPlaybackSuspended={isPlaybackSuspended}
          isVideoStory={isVideoStory}
          isMediaReady={!isAwaitingImage}
          videoElement={videoElement}
          onAdvance={goNext}
        />

        <Box
          sx={{
            position: "absolute",
            top: 20,
            left: 16,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            // Taps must fall through to the prev/next navigation zones underneath.
            pointerEvents: "none",
          }}
        >
          <Avatar
            src={getStoryAvatarSrc(
              storyGroup.authorKind,
              storyGroup.actorImage,
            )}
            alt={storyGroup.actorName}
            sx={{ width: 32, height: 32 }}
          />
          <Typography
            variant="body2"
            color="white"
            fontWeight={600}
            sx={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}
          >
            {storyGroup.actorName}
          </Typography>
        </Box>

        {isVideoStory && (
          <IconButton
            onClick={handleMuteToggle}
            aria-label={t(
              isMuted ? Resources.Common.Unmute : Resources.Common.Mute,
            )}
            sx={{
              position: "absolute",
              top: 16,
              // Next to the pause button on desktop; in its slot on mobile, where the pause
              // button doesn't render (hold-to-pause covers pausing there).
              right: isMobile ? 56 : 96,
              zIndex: 2,
              color: "white",
            }}
          >
            {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </IconButton>
        )}

        {!isMobile && (
          <IconButton
            onClick={() => setIsPaused((prev) => !prev)}
            aria-label={t(
              isPaused ? Resources.Common.Play : Resources.Common.Pause,
            )}
            sx={{
              position: "absolute",
              top: 16,
              right: 56,
              zIndex: 2,
              color: "white",
            }}
          >
            {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
          </IconButton>
        )}

        <IconButton
          onClick={onClose}
          aria-label={t(Resources.Common.Close)}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 2,
            color: "white",
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Keyed by source: switching sources remounts only this content box, fading the new
            source in over the persistent black Dialog — the player never closes mid-session.
            Kept just inside the open reveal so the content is fully opaque when the circle
            settles. */}
        <Fade key={storyGroup.actorId} in appear timeout={200}>
          <Box
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerCancel}
            onPointerCancel={handlePointerCancel}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              cursor: "pointer",
              position: "relative",
            }}
          >
            {story.videoUrl ? (
              // biome-ignore lint/a11y/useMediaCaption: user-uploaded story videos carry no caption tracks
              <Box
                component="video"
                key={story.id}
                ref={setVideoElement}
                src={story.videoUrl}
                poster={story.imageUrl ?? undefined}
                muted={isMuted}
                playsInline
                onEnded={goNext}
                // A dead CDN URL (expired signature, network failure) would otherwise never fire
                // onEnded and stall the autoplay chain at 0% — skip to the next story instead.
                onError={goNext}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              story.imageUrl && (
                <>
                  <Box
                    component="img"
                    // Remounts per story so load state and events can't leak across stories.
                    key={story.id}
                    // Cached images can be complete before React attaches onLoad — the ref
                    // callback (closing over the CURRENT story) catches that synchronously.
                    ref={(element: HTMLImageElement | null) => {
                      if (element?.complete && element.naturalWidth > 0) {
                        setReadyImageStoryId(story.id);
                      }
                    }}
                    src={story.imageUrl}
                    alt=""
                    onLoad={() => setReadyImageStoryId(story.id)}
                    onError={handleImageError}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                  {isAwaitingImage && (
                    <CircularProgress
                      size={32}
                      sx={{ color: "white", pointerEvents: "none" }}
                    />
                  )}
                </>
              )
            )}

            {story.buttonLabel && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: story.text ? 88 : 48,
                  left: 16,
                  right: 16,
                  zIndex: 1,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleCtaClick}
                  onPointerDown={stopPointerPropagation}
                  onPointerUp={stopPointerPropagation}
                  sx={{
                    borderRadius: 6,
                    px: 4,
                    py: 1.2,
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    bgcolor: "white",
                    color: "black",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.9)",
                    },
                  }}
                >
                  {story.buttonLabel}
                </Button>
              </Box>
            )}

            {story.text && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 48,
                  left: 16,
                  right: 16,
                  zIndex: 1,
                }}
              >
                <Typography
                  color="white"
                  variant="body1"
                  sx={{
                    textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                  }}
                >
                  {story.text}
                </Typography>
              </Box>
            )}
          </Box>
        </Fade>

        {!isMobile && (
          <>
            <IconButton
              onClick={goPrev}
              aria-label={t(Resources.Common.Back)}
              sx={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                color: "white",
                bgcolor: "rgba(255,255,255,0.15)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.3)",
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={goNext}
              aria-label={t(Resources.Common.Next)}
              sx={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                color: "white",
                bgcolor: "rgba(255,255,255,0.15)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.3)",
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}
      </Dialog>

      {story.buttonLabel && (
        <SwipeableDrawer
          anchor="bottom"
          open={isSheetOpen}
          onOpen={() => setIsSheetOpen(true)}
          onClose={() => setIsSheetOpen(false)}
          disableSwipeToOpen
          sx={{ zIndex: 1400 }}
          slotProps={{
            paper: {
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                maxHeight: "60vh",
                px: 3,
                pb: 4,
                pt: 1,
              },
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: "grey.400",
              borderRadius: 2,
              mx: "auto",
              mb: 2,
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 1,
            }}
          >
            <IconButton
              onClick={() => setIsSheetOpen(false)}
              aria-label={t(Resources.Common.Close)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {story.sheetImageUrl && (
            <Box
              component="img"
              src={story.sheetImageUrl}
              alt=""
              sx={{
                width: "100%",
                maxHeight: 200,
                objectFit: "cover",
                borderRadius: 2,
                mb: 2,
              }}
            />
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            {story.sheetTitle && (
              <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
                {story.sheetTitle}
              </Typography>
            )}
            {(story.sheetPrice || story.sheetPriceNote) && (
              <Box sx={{ textAlign: "right", ml: 2, flexShrink: 0 }}>
                {story.sheetPrice && (
                  <Typography variant="h6" fontWeight={700}>
                    {story.sheetPrice}
                  </Typography>
                )}
                {story.sheetPriceNote && (
                  <Typography variant="caption" color="text.secondary">
                    {story.sheetPriceNote}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {story.sheetDescription && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {story.sheetDescription}
            </Typography>
          )}

          {story.buttonUrl && (
            <Button
              variant="contained"
              fullWidth
              size="large"
              href={story.buttonUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderRadius: 6,
                py: 1.5,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              {story.buttonLabel}
            </Button>
          )}
        </SwipeableDrawer>
      )}
    </>
  );
};
